/**
 * Tools, AI actions, approvals, handoff packets and simulations (boards 10, 11 and 13) in the four
 * launch languages. Arabic, Hindi and Urdu are pending review by native speakers.
 */
export const toolsEn = {
  nav: 'Tools and permissions',
  title: 'Tools and permissions',
  description:
    'Control what your AI agents can do, what data they can use, and when a person must approve first.',
  brand: 'Brand',
  noBrands: 'Add a brand first.',
  loading: 'Loading tools',
  optional: 'optional',
  agent: 'Agent',
  agentNone: 'No AI agent is set up for this brand yet.',
  agentPublished: 'Published v{{version}}',
  agentDraft: 'Not published',
  editGrants: 'Choose this agent’s tools',
  noManage: 'You can look, but only people who manage tools can change them.',
  killScope: 'Tool: {{name}}',
  risk: {
    read: 'Reads data',
    write: 'Changes data',
    money: 'Moves money',
    irreversible: 'Cannot be undone',
  },
  permission: {
    automatic: 'Runs automatically',
    customer: 'Customer approves',
    staff: 'Staff approve',
    review: 'Waiting for review',
    off: 'Off',
    stopped: 'Stopped',
  },
  permissionHint: {
    automatic: 'Within the budgets below. A change still gets a receipt.',
    customer: 'The customer approves the exact change in the chat first.',
    staff: 'A team member with permission approves it first.',
    review: 'Nothing can call it until a person reviews it.',
    off: 'Not offered to any agent.',
    stopped: 'A kill switch refuses every call until it is cleared.',
  },
  states: {
    awaiting_approval: 'Waiting for approval',
    approved: 'Approved',
    executing: 'Being done',
    completed: 'Done',
    failed: 'Not done',
    unknown: 'Result not confirmed',
    declined: 'Declined',
    expired: 'Expired',
    superseded: 'Replaced',
    cancelled: 'Stopped',
    denied: 'Refused',
  },
  list: {
    title: 'Tools',
    description:
      'Each call is checked again when it happens: tool status and policy, kill switches, budgets and who handles the conversation.',
    builtin: 'Built into Hamnivo',
    from: 'From {{server}}',
    emptyTitle: 'No tools yet',
    emptyDescription:
      'Add a built-in tool or connect a service to let the AI look things up or ask for changes.',
    open: 'Open {{tool}}',
    columns: {
      tool: 'Tool',
      scope: 'What it can do',
      permission: 'Permission',
      changed: 'Last changed',
      details: 'Details',
    },
  },
  add: {
    button: 'Add a built-in tool',
    title: 'Add a built-in tool',
    description:
      'Built-in tools act on Hamnivo’s own records, or on the demo order service in demo set-ups.',
    label: 'Tool',
    choose: 'Choose a tool',
    demo: 'demo',
    added: 'already added',
    unavailable: 'not available here',
    submit: 'Add tool',
    done: '{{tool}} added.',
  },
  drawer: {
    permission: 'Permission',
    revision: 'Revision {{revision}}',
    reviewed: 'reviewed {{time}}',
    service: 'Connected service',
    credential: 'Credential',
    noCredential: 'None',
    hosts: 'Allowed hosts',
    hostsHint: 'One host per line, such as orders.example.com. Calls can reach only these hosts.',
    remote: 'What the service says (never shown to the AI)',
    remoteNote: 'Shown as the service sent it. The AI sees only the description at the top.',
    reviewNote: 'Check what the tool does and set its policy below, then turn it on.',
    review: 'Review and turn on',
    settings: 'Settings',
    on: 'Offer this tool to agents',
    onHint: 'When off, no agent can call it.',
    risk: 'What it can do',
    approver: 'Who approves',
    approverCustomer: 'The customer',
    approverCustomerHint: 'Only for their own low-risk requests, in the chat.',
    approverStaff: 'A team member',
    approverStaffHint: 'People with “Approve AI actions” and the permission below.',
    autopilot: 'Can run without approval',
    autopilotHint:
      'Only reads and low-risk changes, within the budgets. Money and irreversible actions always need staff.',
    stepUp: 'The customer confirms who they are first',
    stepUpHint: 'They must have signed in on the website within the last few minutes.',
    requiredPermission: 'Extra permission for approvers',
    requiredPermissionHint: 'For example commerce.refunds.approve.',
    approvalMinutes: 'Approval stays open (minutes)',
    approvalMinutesHint: 'Empty: 30 minutes for customers, 24 hours for staff.',
    timeout: 'Time limit (seconds)',
    maxOutput: 'Largest answer (KB)',
    save: 'Save changes',
    saved:
      'Saved. Any approval still open for this tool was cancelled, because the policy changed.',
    conflict:
      'Someone changed this tool first. The latest version is shown; make your change again.',
    stop: 'Stop this tool',
    stopTitle: 'Stop “{{tool}}”?',
    stopBody:
      'Every call is refused and approved actions are not sent until the switch is cleared. Actions already sent are still checked.',
    stopReason: 'Reason',
    stoppedNow: '{{tool}} is stopped.',
    stopped: 'Stopped: see Stop AI',
    recent: 'Recent actions',
  },
  errors: {
    number: 'Enter a whole number from {{min}} to {{max}}.',
    hosts: 'Up to 20 hosts.',
    highRiskApprover: 'Money and irreversible actions must be approved by staff.',
    highRiskAutopilot: 'Money and irreversible actions never run without approval.',
    label: 'Enter a name.',
    url: 'Enter the server’s full address, starting with https://.',
    token: 'A token has at least 8 characters.',
    reason: 'Say why, in a few words.',
  },
  servers: {
    title: 'Connected services (MCP)',
    description:
      'Remote MCP servers over HTTPS. New tools wait for review, and nothing a service says reaches the AI as an instruction.',
    loading: 'Loading services',
    empty: 'No services connected.',
    connect: 'Connect a service',
    columns: { name: 'Service' },
    status: { active: 'Active', disabled: 'Off' },
    toolCount: 'Tools: {{total}}',
    pending: 'Waiting for review: {{total}}',
    checked: 'Tools checked {{time}}',
    never: 'Tools not checked yet',
    lastError: 'The last check failed: {{error}}',
    sync: 'Check tools',
    syncLabel: 'Check the tools of {{name}}',
    synced: 'Checked: {{added}} new, {{changed}} changed, {{removed}} removed.',
    skipped: 'Not added safely: {{total}}.',
    turnOff: 'Turn off',
    turnOn: 'Turn on',
    dialogTitle: 'Connect a service',
    dialogDescription:
      'Hamnivo talks to MCP servers over Streamable HTTP. Its tools wait for review before any agent can use them.',
    label: 'Name',
    url: 'Server address',
    urlHint: 'For example https://orders.example.com/mcp',
    token: 'Access token',
    tokenHint: 'Stored encrypted. Only its last characters are ever shown.',
    submit: 'Connect',
    connected: '{{name}} connected. Check its tools next.',
  },
  settings: {
    title: 'Automatic actions',
    description:
      'Budgets for actions the AI may take without approval. Kill switches stop them at once.',
    loading: 'Loading settings',
    enabled: 'Allow automatic actions',
    enabledHint: 'When off, every change waits for approval.',
    limit: 'Most automatic actions per day',
    limitHint: 'For the whole workspace. Beyond it, changes wait for approval and lookups stop.',
    save: 'Save',
    saved: 'Settings saved.',
  },
  log: {
    title: 'Recent AI actions',
    description: 'What the AI proposed, who decided, and what the receipt says.',
    loading: 'Loading actions',
    empty: 'No actions yet.',
    open: 'Open conversation',
    columns: {
      when: 'When',
      action: 'Action',
      state: 'State',
      decided: 'Decided by',
    },
    by: {
      customer: 'Customer',
      staff: 'Team member',
      autopilot: 'Automatic',
    },
  },
  grants: {
    title: 'Tools',
    hint: 'What this agent may use. Each tool’s own policy still decides who approves.',
    hidden: 'You can’t see the tools of this brand.',
    none: 'This brand has no tools yet.',
    manage: 'Manage tools',
    autopilot: 'May run without approval',
    autopilotHint: 'Off: this agent always asks, even where the tool could run on its own.',
    inReview: 'This tool waits for review; the agent can’t use it until then.',
    unknown: 'A tool that no longer exists here:',
    remove: 'Remove',
    tooMany: 'An agent can use up to 30 tools.',
    maxCalls: 'Tool calls per message',
    maxCallsHint: 'From 1 to 10. Calls beyond it are refused.',
    maxAutopilot: 'Automatic actions per conversation',
    maxAutopilotHint: 'From 0 to 50. Beyond it, changes wait for approval.',
  },
  sims: {
    title: 'Test before publishing',
    description:
      'Replay sample conversations against this draft and the published version, with a test model and fake tools. Nothing is sent and no customer data is used.',
    loading: 'Loading tests',
    empty: 'No test suites yet.',
    add: 'Add a test suite',
    addTitle: 'Add a test suite',
    editTitle: 'Edit the test suite',
    dialogDescription:
      'Each case lists customer messages, what each tool answers, and what you expect.',
    name: 'Name',
    requiredLabel: 'Publishing waits until the draft passes',
    requiredHint: 'The exact draft must pass this suite before it can go live.',
    cases: 'Cases (JSON)',
    casesHint:
      'A list of cases with id, name, messages, tools (fake answers) and expect (outcome, actions).',
    save: 'Save suite',
    edit: 'Edit',
    delete: 'Delete',
    deleteTitle: 'Delete the test suite “{{name}}”?',
    deleteBody: 'Its results are deleted too. Publishing no longer waits for it.',
    run: 'Run on this draft',
    runLabel: 'Run {{name}} on this draft',
    required: 'Required before publishing',
    optional: 'Optional',
    caseCount: 'Cases: {{total}}',
    lastRun: 'Last run {{time}}: {{status}}',
    notRun: 'Not run on this draft yet',
    noDraft: 'Save a draft first; tests run on the draft.',
    results: 'Results',
    summary: 'Cases: {{cases}} · changed: {{changed}} · failing: {{failed}}',
    testOnly: 'Test only: nothing was sent',
    draft: 'This draft',
    published: 'Published version',
    noPublished: 'Nothing is published yet.',
    noActions: 'No actions',
    changes: 'Changes from the published version',
    noChanges: 'No changes',
    failures: 'Expectations not met',
    replies: 'Replies of the draft',
    status: { passed: 'Passed', failed: 'Failed' },
    outcomes: {
      replied: 'Replied',
      handoff: 'Handed to a person',
      blocked: 'Held back by a guard',
      error: 'Error',
    },
    decisions: {
      executed: 'Done',
      approval: 'Asks for approval',
      denied: 'Refused',
      invalid: 'Incomplete request',
      failed: 'Failed',
    },
    errors: {
      name: 'Enter a name.',
      cases: 'Enter a JSON list with at least one case.',
    },
  },
  inbox: {
    title: 'AI actions',
    empty: 'The AI has not proposed any actions here.',
    from: 'from',
    to: 'to',
    amount: 'Amount',
    receipt: 'Receipt: {{outcome}}',
    receiptOutcomes: {
      pending: 'sent',
      succeeded: 'done',
      failed: 'not done',
      unknown: 'not confirmed',
    },
    decided: '{{decision}} by {{who}} · {{time}}',
    decisions: { approved: 'Approved', declined: 'Declined' },
    customerApproves: 'The customer approves this in the chat.',
    staffApproves: 'A team member approves this.',
    stepUp: 'A team member approves this, after a fresh sign-in.',
    expires: 'Open until {{time}}.',
    notConfirmed: 'Sent, but the result is not confirmed. Do not do it again by hand.',
    approve: 'Approve',
    decline: 'Decline',
    approved: 'Approved. It is being sent.',
    declined: 'Declined. Nothing was changed.',
    check: 'Check status',
    checking: 'Checking with the service.',
    resolve: 'Record the result',
    resolveTitle: 'Record what happened',
    resolveDescription: 'Only when Hamnivo cannot check the service itself. Nothing is sent again.',
    resolveOutcome: 'What happened',
    resolveDone: 'It was done',
    resolveNotDone: 'It was not done',
    resolveNote: 'What you saw (kept in the audit log)',
    resolveNoteError: 'Say what you saw, in a few words.',
    resolveSubmit: 'Record result',
    resolved: 'Result recorded.',
  },
  handoff: {
    title: 'Handoff summary',
    none: 'The AI has not handed this conversation over.',
    epoch: 'Handover {{epoch}}',
    request: 'Customer’s request',
    attempts: 'What the AI did',
    pending: 'Still open',
    pendingStates: {
      awaiting_approval: 'waiting for approval',
      approved: 'approved, not sent yet',
      executing: 'sent, waiting for the result',
      unknown: 'result not confirmed, being checked',
      superseded: 'cancelled by the handover',
    },
    facts: 'Customer facts',
    sources: {
      tool: 'tool result',
      memory: 'customer memory',
      identity: 'verified by the website',
      language: 'language setting',
      order: 'order record',
    },
    recorded: 'Recorded {{time}} from Hamnivo’s records.',
  },
  runs: {
    stop: 'Stop this run',
    stopTitle: 'Stop this AI run?',
    stopBody:
      'Tool calls in flight are cancelled and no reply is posted. A change already sent is checked, never sent again.',
    stopped: 'Stop requested.',
  },
} as const;

type Strings<T> = { [K in keyof T]: T[K] extends string ? string : Strings<T[K]> };
export type ToolsText = Strings<typeof toolsEn>;

export const toolsAr: ToolsText = {
  nav: 'الأدوات والصلاحيات',
  title: 'الأدوات والصلاحيات',
  description:
    'حدّد ما يمكن لوكلاء الذكاء الاصطناعي فعله، والبيانات التي يستخدمونها، ومتى يجب أن يوافق شخص أولاً.',
  brand: 'العلامة التجارية',
  noBrands: 'أضف علامة تجارية أولاً.',
  loading: 'جارٍ تحميل الأدوات',
  optional: 'اختياري',
  agent: 'الوكيل',
  agentNone: 'لم يُعدّ وكيل ذكاء اصطناعي لهذه العلامة بعد.',
  agentPublished: 'منشور v{{version}}',
  agentDraft: 'غير منشور',
  editGrants: 'اختر أدوات هذا الوكيل',
  noManage: 'يمكنك الاطلاع، لكن تغيير الأدوات لمن يديرها فقط.',
  killScope: 'أداة: {{name}}',
  risk: {
    read: 'يقرأ البيانات',
    write: 'يغيّر البيانات',
    money: 'يحرّك المال',
    irreversible: 'لا يمكن التراجع عنه',
  },
  permission: {
    automatic: 'يعمل تلقائيًا',
    customer: 'العميل يوافق',
    staff: 'الفريق يوافق',
    review: 'بانتظار المراجعة',
    off: 'متوقف',
    stopped: 'موقوف',
  },
  permissionHint: {
    automatic: 'ضمن الحدود أدناه. أي تغيير يحصل على إيصال.',
    customer: 'يوافق العميل على التغيير نفسه في المحادثة أولاً.',
    staff: 'يوافق عضو مخوّل من الفريق أولاً.',
    review: 'لا يمكن استدعاؤها حتى يراجعها شخص.',
    off: 'لا تُعرض على أي وكيل.',
    stopped: 'مفتاح إيقاف يرفض كل استدعاء حتى يُزال.',
  },
  states: {
    awaiting_approval: 'بانتظار الموافقة',
    approved: 'تمت الموافقة',
    executing: 'قيد التنفيذ',
    completed: 'تم',
    failed: 'لم يتم',
    unknown: 'النتيجة غير مؤكدة',
    declined: 'مرفوض',
    expired: 'منتهي الصلاحية',
    superseded: 'استُبدل',
    cancelled: 'أُوقف',
    denied: 'رُفض',
  },
  list: {
    title: 'الأدوات',
    description:
      'يُفحص كل استدعاء من جديد لحظة حدوثه: حالة الأداة وسياستها ومفاتيح الإيقاف والحدود ومن يتولى المحادثة.',
    builtin: 'مدمجة في Hamnivo',
    from: 'من {{server}}',
    emptyTitle: 'لا توجد أدوات بعد',
    emptyDescription:
      'أضف أداة مدمجة أو اربط خدمة ليتمكن الذكاء الاصطناعي من البحث أو طلب تغييرات.',
    open: 'افتح {{tool}}',
    columns: {
      tool: 'الأداة',
      scope: 'ما يمكنها فعله',
      permission: 'الصلاحية',
      changed: 'آخر تغيير',
      details: 'التفاصيل',
    },
  },
  add: {
    button: 'أضف أداة مدمجة',
    title: 'أضف أداة مدمجة',
    description:
      'تعمل الأدوات المدمجة على سجلات Hamnivo نفسها، أو على خدمة الطلبات التجريبية في بيئات العرض.',
    label: 'الأداة',
    choose: 'اختر أداة',
    demo: 'تجريبية',
    added: 'مضافة مسبقًا',
    unavailable: 'غير متاحة هنا',
    submit: 'أضف الأداة',
    done: 'أُضيفت {{tool}}.',
  },
  drawer: {
    permission: 'الصلاحية',
    revision: 'المراجعة {{revision}}',
    reviewed: 'رُوجعت {{time}}',
    service: 'الخدمة المرتبطة',
    credential: 'بيانات الاعتماد',
    noCredential: 'لا يوجد',
    hosts: 'المضيفات المسموح بها',
    hostsHint: 'مضيف في كل سطر، مثل orders.example.com. لا تصل الاستدعاءات إلا إليها.',
    remote: 'ما تقوله الخدمة (لا يُعرض على الذكاء الاصطناعي أبدًا)',
    remoteNote: 'كما أرسلته الخدمة. يرى الذكاء الاصطناعي الوصف في الأعلى فقط.',
    reviewNote: 'تحقق مما تفعله الأداة واضبط سياستها أدناه، ثم شغّلها.',
    review: 'راجِع وشغّل',
    settings: 'الإعدادات',
    on: 'اعرض هذه الأداة على الوكلاء',
    onHint: 'عند إيقافها لا يستطيع أي وكيل استدعاءها.',
    risk: 'ما يمكنها فعله',
    approver: 'من يوافق',
    approverCustomer: 'العميل',
    approverCustomerHint: 'لطلباته منخفضة المخاطر فقط، داخل المحادثة.',
    approverStaff: 'عضو من الفريق',
    approverStaffHint: 'من لديهم «الموافقة على إجراءات الذكاء الاصطناعي» والصلاحية أدناه.',
    autopilot: 'يمكن تشغيلها دون موافقة',
    autopilotHint:
      'للقراءة والتغييرات منخفضة المخاطر فقط، ضمن الحدود. المال والإجراءات التي لا رجعة فيها تحتاج دائمًا إلى الفريق.',
    stepUp: 'يؤكد العميل هويته أولاً',
    stepUpHint: 'يجب أن يكون قد سجّل الدخول في الموقع خلال الدقائق الأخيرة.',
    requiredPermission: 'صلاحية إضافية للموافِقين',
    requiredPermissionHint: 'مثل commerce.refunds.approve.',
    approvalMinutes: 'مدة بقاء الموافقة مفتوحة (بالدقائق)',
    approvalMinutesHint: 'فارغ: 30 دقيقة للعملاء و24 ساعة للفريق.',
    timeout: 'المهلة (بالثواني)',
    maxOutput: 'أكبر إجابة (كيلوبايت)',
    save: 'احفظ التغييرات',
    saved: 'تم الحفظ. أُلغيت الموافقات المفتوحة لهذه الأداة لأن السياسة تغيّرت.',
    conflict: 'غيّر شخص آخر هذه الأداة أولاً. تظهر أحدث نسخة؛ أعد تغييرك.',
    stop: 'أوقف هذه الأداة',
    stopTitle: 'إيقاف «{{tool}}»؟',
    stopBody:
      'يُرفض كل استدعاء ولا تُرسل الإجراءات الموافق عليها حتى يُزال المفتاح. يستمر التحقق من الإجراءات المرسلة.',
    stopReason: 'السبب',
    stoppedNow: 'أُوقفت {{tool}}.',
    stopped: 'موقوفة: انظر إيقاف الذكاء الاصطناعي',
    recent: 'آخر الإجراءات',
  },
  errors: {
    number: 'أدخل عددًا صحيحًا من {{min}} إلى {{max}}.',
    hosts: 'حتى 20 مضيفًا.',
    highRiskApprover: 'المال والإجراءات التي لا رجعة فيها يجب أن يوافق عليها الفريق.',
    highRiskAutopilot: 'المال والإجراءات التي لا رجعة فيها لا تعمل أبدًا دون موافقة.',
    label: 'أدخل اسمًا.',
    url: 'أدخل العنوان الكامل للخادم، بدءًا من https://.',
    token: 'الرمز 8 أحرف على الأقل.',
    reason: 'اذكر السبب ببضع كلمات.',
  },
  servers: {
    title: 'الخدمات المرتبطة (MCP)',
    description:
      'خوادم MCP بعيدة عبر HTTPS. تنتظر الأدوات الجديدة المراجعة، ولا يصل أي شيء تقوله الخدمة إلى الذكاء الاصطناعي كتعليمات.',
    loading: 'جارٍ تحميل الخدمات',
    empty: 'لا توجد خدمات مرتبطة.',
    connect: 'اربط خدمة',
    columns: { name: 'الخدمة' },
    status: { active: 'نشطة', disabled: 'متوقفة' },
    toolCount: 'الأدوات: {{total}}',
    pending: 'بانتظار المراجعة: {{total}}',
    checked: 'فُحصت الأدوات {{time}}',
    never: 'لم تُفحص الأدوات بعد',
    lastError: 'فشل الفحص الأخير: {{error}}',
    sync: 'افحص الأدوات',
    syncLabel: 'افحص أدوات {{name}}',
    synced: 'تم الفحص: {{added}} جديدة، {{changed}} متغيّرة، {{removed}} أُزيلت.',
    skipped: 'لم تُضف بأمان: {{total}}.',
    turnOff: 'أوقف',
    turnOn: 'شغّل',
    dialogTitle: 'اربط خدمة',
    dialogDescription:
      'يتصل Hamnivo بخوادم MCP عبر Streamable HTTP. تنتظر أدواتها المراجعة قبل أن يستخدمها أي وكيل.',
    label: 'الاسم',
    url: 'عنوان الخادم',
    urlHint: 'مثل https://orders.example.com/mcp',
    token: 'رمز الوصول',
    tokenHint: 'يُخزّن مشفّرًا. لا يظهر منه إلا آخر أحرفه.',
    submit: 'اربط',
    connected: 'رُبطت {{name}}. افحص أدواتها بعد ذلك.',
  },
  settings: {
    title: 'الإجراءات التلقائية',
    description:
      'حدود الإجراءات التي يمكن للذكاء الاصطناعي اتخاذها دون موافقة. مفاتيح الإيقاف توقفها فورًا.',
    loading: 'جارٍ تحميل الإعدادات',
    enabled: 'اسمح بالإجراءات التلقائية',
    enabledHint: 'عند الإيقاف ينتظر كل تغيير الموافقة.',
    limit: 'أقصى عدد للإجراءات التلقائية يوميًا',
    limitHint: 'لمساحة العمل كلها. بعده تنتظر التغييرات الموافقة وتتوقف عمليات البحث.',
    save: 'احفظ',
    saved: 'حُفظت الإعدادات.',
  },
  log: {
    title: 'آخر إجراءات الذكاء الاصطناعي',
    description: 'ما اقترحه الذكاء الاصطناعي، ومن قرّر، وما يقوله الإيصال.',
    loading: 'جارٍ تحميل الإجراءات',
    empty: 'لا توجد إجراءات بعد.',
    open: 'افتح المحادثة',
    columns: {
      when: 'متى',
      action: 'الإجراء',
      state: 'الحالة',
      decided: 'قرّر',
    },
    by: {
      customer: 'العميل',
      staff: 'عضو الفريق',
      autopilot: 'تلقائي',
    },
  },
  grants: {
    title: 'الأدوات',
    hint: 'ما يمكن لهذا الوكيل استخدامه. سياسة كل أداة تحدد من يوافق.',
    hidden: 'لا يمكنك رؤية أدوات هذه العلامة.',
    none: 'لا توجد أدوات لهذه العلامة بعد.',
    manage: 'أدِر الأدوات',
    autopilot: 'يمكن تشغيلها دون موافقة',
    autopilotHint: 'عند الإيقاف يطلب هذا الوكيل الموافقة دائمًا، حتى حيث يمكن للأداة العمل وحدها.',
    inReview: 'هذه الأداة بانتظار المراجعة؛ لا يستطيع الوكيل استخدامها حتى ذلك الحين.',
    unknown: 'أداة لم تعد موجودة هنا:',
    remove: 'أزل',
    tooMany: 'يمكن للوكيل استخدام حتى 30 أداة.',
    maxCalls: 'استدعاءات الأدوات لكل رسالة',
    maxCallsHint: 'من 1 إلى 10. تُرفض الاستدعاءات الزائدة.',
    maxAutopilot: 'الإجراءات التلقائية لكل محادثة',
    maxAutopilotHint: 'من 0 إلى 50. بعدها تنتظر التغييرات الموافقة.',
  },
  sims: {
    title: 'اختبر قبل النشر',
    description:
      'أعد تشغيل محادثات نموذجية على هذه المسودة والنسخة المنشورة بنموذج اختبار وأدوات وهمية. لا يُرسل شيء ولا تُستخدم بيانات العملاء.',
    loading: 'جارٍ تحميل الاختبارات',
    empty: 'لا توجد مجموعات اختبار بعد.',
    add: 'أضف مجموعة اختبار',
    addTitle: 'أضف مجموعة اختبار',
    editTitle: 'عدّل مجموعة الاختبار',
    dialogDescription: 'كل حالة تذكر رسائل العميل وما تجيب به كل أداة وما تتوقعه.',
    name: 'الاسم',
    requiredLabel: 'النشر ينتظر نجاح المسودة',
    requiredHint: 'يجب أن تنجح المسودة نفسها في هذه المجموعة قبل نشرها.',
    cases: 'الحالات (JSON)',
    casesHint:
      'قائمة حالات فيها id وname وmessages وtools (إجابات وهمية) وexpect (النتيجة والإجراءات).',
    save: 'احفظ المجموعة',
    edit: 'عدّل',
    delete: 'احذف',
    deleteTitle: 'حذف مجموعة الاختبار «{{name}}»؟',
    deleteBody: 'تُحذف نتائجها أيضًا. لن ينتظرها النشر بعد الآن.',
    run: 'شغّل على هذه المسودة',
    runLabel: 'شغّل {{name}} على هذه المسودة',
    required: 'مطلوبة قبل النشر',
    optional: 'اختيارية',
    caseCount: 'الحالات: {{total}}',
    lastRun: 'آخر تشغيل {{time}}: {{status}}',
    notRun: 'لم تُشغّل على هذه المسودة بعد',
    noDraft: 'احفظ مسودة أولاً؛ الاختبارات تعمل على المسودة.',
    results: 'النتائج',
    summary: 'الحالات: {{cases}} · المتغيّرة: {{changed}} · الفاشلة: {{failed}}',
    testOnly: 'اختبار فقط: لم يُرسل شيء',
    draft: 'هذه المسودة',
    published: 'النسخة المنشورة',
    noPublished: 'لم يُنشر شيء بعد.',
    noActions: 'لا إجراءات',
    changes: 'التغييرات عن النسخة المنشورة',
    noChanges: 'لا تغييرات',
    failures: 'توقعات لم تتحقق',
    replies: 'ردود المسودة',
    status: { passed: 'نجحت', failed: 'فشلت' },
    outcomes: {
      replied: 'ردّ',
      handoff: 'حُوّلت إلى شخص',
      blocked: 'أوقفها حارس',
      error: 'خطأ',
    },
    decisions: {
      executed: 'تم',
      approval: 'يطلب الموافقة',
      denied: 'رُفض',
      invalid: 'طلب ناقص',
      failed: 'فشل',
    },
    errors: {
      name: 'أدخل اسمًا.',
      cases: 'أدخل قائمة JSON فيها حالة واحدة على الأقل.',
    },
  },
  inbox: {
    title: 'إجراءات الذكاء الاصطناعي',
    empty: 'لم يقترح الذكاء الاصطناعي أي إجراء هنا.',
    from: 'من',
    to: 'إلى',
    amount: 'المبلغ',
    receipt: 'الإيصال: {{outcome}}',
    receiptOutcomes: {
      pending: 'أُرسل',
      succeeded: 'تم',
      failed: 'لم يتم',
      unknown: 'غير مؤكد',
    },
    decided: '{{decision}} من {{who}} · {{time}}',
    decisions: { approved: 'وافق', declined: 'رفض' },
    customerApproves: 'يوافق العميل على هذا في المحادثة.',
    staffApproves: 'يوافق عضو من الفريق على هذا.',
    stepUp: 'يوافق عضو من الفريق على هذا بعد تسجيل دخول جديد.',
    expires: 'مفتوح حتى {{time}}.',
    notConfirmed: 'أُرسل لكن النتيجة غير مؤكدة. لا تكرره يدويًا.',
    approve: 'وافق',
    decline: 'ارفض',
    approved: 'تمت الموافقة. يجري الإرسال.',
    declined: 'تم الرفض. لم يتغيّر شيء.',
    check: 'تحقق من الحالة',
    checking: 'نتحقق مع الخدمة.',
    resolve: 'سجّل النتيجة',
    resolveTitle: 'سجّل ما حدث',
    resolveDescription: 'فقط عندما لا يستطيع Hamnivo التحقق من الخدمة بنفسه. لا يُرسل شيء مرة أخرى.',
    resolveOutcome: 'ما الذي حدث',
    resolveDone: 'تم',
    resolveNotDone: 'لم يتم',
    resolveNote: 'ما رأيته (يُحفظ في سجل التدقيق)',
    resolveNoteError: 'اذكر ما رأيته ببضع كلمات.',
    resolveSubmit: 'سجّل النتيجة',
    resolved: 'سُجّلت النتيجة.',
  },
  handoff: {
    title: 'ملخص التحويل',
    none: 'لم يحوّل الذكاء الاصطناعي هذه المحادثة.',
    epoch: 'التحويل {{epoch}}',
    request: 'طلب العميل',
    attempts: 'ما فعله الذكاء الاصطناعي',
    pending: 'ما زال مفتوحًا',
    pendingStates: {
      awaiting_approval: 'بانتظار الموافقة',
      approved: 'تمت الموافقة ولم يُرسل بعد',
      executing: 'أُرسل وبانتظار النتيجة',
      unknown: 'النتيجة غير مؤكدة وقيد التحقق',
      superseded: 'أُلغي بسبب التحويل',
    },
    facts: 'معلومات العميل',
    sources: {
      tool: 'نتيجة أداة',
      memory: 'ذاكرة العميل',
      identity: 'موثّقة من الموقع',
      language: 'إعداد اللغة',
      order: 'سجل الطلب',
    },
    recorded: 'سُجّل {{time}} من سجلات Hamnivo.',
  },
  runs: {
    stop: 'أوقف هذا التشغيل',
    stopTitle: 'إيقاف تشغيل الذكاء الاصطناعي هذا؟',
    stopBody:
      'تُلغى استدعاءات الأدوات الجارية ولا يُنشر أي رد. يُتحقق من أي تغيير أُرسل، ولا يُرسل مرة أخرى أبدًا.',
    stopped: 'طُلب الإيقاف.',
  },
};

export const toolsHi: ToolsText = {
  nav: 'टूल और अनुमतियाँ',
  title: 'टूल और अनुमतियाँ',
  description:
    'तय करें कि आपके एआई एजेंट क्या कर सकते हैं, कौन-सा डेटा इस्तेमाल कर सकते हैं, और कब किसी व्यक्ति की मंज़ूरी पहले ज़रूरी है।',
  brand: 'ब्रांड',
  noBrands: 'पहले एक ब्रांड जोड़ें।',
  loading: 'टूल लोड हो रहे हैं',
  optional: 'वैकल्पिक',
  agent: 'एजेंट',
  agentNone: 'इस ब्रांड के लिए अभी कोई एआई एजेंट सेट नहीं है।',
  agentPublished: 'प्रकाशित v{{version}}',
  agentDraft: 'प्रकाशित नहीं',
  editGrants: 'इस एजेंट के टूल चुनें',
  noManage: 'आप देख सकते हैं, पर टूल बदलना सिर्फ़ उन्हीं का काम है जो टूल संभालते हैं।',
  killScope: 'टूल: {{name}}',
  risk: {
    read: 'डेटा पढ़ता है',
    write: 'डेटा बदलता है',
    money: 'पैसे का लेन-देन करता है',
    irreversible: 'वापस नहीं हो सकता',
  },
  permission: {
    automatic: 'अपने-आप चलता है',
    customer: 'ग्राहक मंज़ूरी देता है',
    staff: 'टीम मंज़ूरी देती है',
    review: 'समीक्षा बाक़ी',
    off: 'बंद',
    stopped: 'रोका गया',
  },
  permissionHint: {
    automatic: 'नीचे दी गई सीमाओं में। हर बदलाव की रसीद बनती है।',
    customer: 'ग्राहक पहले चैट में ठीक उसी बदलाव को मंज़ूर करता है।',
    staff: 'अनुमति वाला टीम सदस्य पहले मंज़ूरी देता है।',
    review: 'किसी व्यक्ति की समीक्षा तक इसे कोई नहीं बुला सकता।',
    off: 'किसी एजेंट को नहीं दिया जाता।',
    stopped: 'किल स्विच हटने तक हर कॉल मना होती है।',
  },
  states: {
    awaiting_approval: 'मंज़ूरी का इंतज़ार',
    approved: 'मंज़ूर',
    executing: 'किया जा रहा है',
    completed: 'हो गया',
    failed: 'नहीं हुआ',
    unknown: 'नतीजा पक्का नहीं',
    declined: 'मना किया',
    expired: 'समय ख़त्म',
    superseded: 'बदल दिया गया',
    cancelled: 'रोका गया',
    denied: 'अस्वीकार',
  },
  list: {
    title: 'टूल',
    description:
      'हर कॉल होते समय दोबारा जाँची जाती है: टूल की स्थिति और नीति, किल स्विच, सीमाएँ और बातचीत कौन संभाल रहा है।',
    builtin: 'Hamnivo में शामिल',
    from: '{{server}} से',
    emptyTitle: 'अभी कोई टूल नहीं',
    emptyDescription: 'कोई शामिल टूल जोड़ें या कोई सेवा जोड़ें, ताकि एआई जानकारी देख सके या बदलाव माँग सके।',
    open: '{{tool}} खोलें',
    columns: {
      tool: 'टूल',
      scope: 'यह क्या कर सकता है',
      permission: 'अनुमति',
      changed: 'आख़िरी बदलाव',
      details: 'विवरण',
    },
  },
  add: {
    button: 'शामिल टूल जोड़ें',
    title: 'शामिल टूल जोड़ें',
    description: 'शामिल टूल Hamnivo के अपने रिकॉर्ड पर, या डेमो सेटअप में डेमो ऑर्डर सेवा पर काम करते हैं।',
    label: 'टूल',
    choose: 'टूल चुनें',
    demo: 'डेमो',
    added: 'पहले से जोड़ा गया',
    unavailable: 'यहाँ उपलब्ध नहीं',
    submit: 'टूल जोड़ें',
    done: '{{tool}} जोड़ा गया।',
  },
  drawer: {
    permission: 'अनुमति',
    revision: 'संशोधन {{revision}}',
    reviewed: '{{time}} को समीक्षा हुई',
    service: 'जुड़ी सेवा',
    credential: 'क्रेडेंशियल',
    noCredential: 'कोई नहीं',
    hosts: 'अनुमत होस्ट',
    hostsHint: 'हर लाइन में एक होस्ट, जैसे orders.example.com। कॉल सिर्फ़ इन्हीं तक पहुँच सकती हैं।',
    remote: 'सेवा क्या कहती है (एआई को कभी नहीं दिखाया जाता)',
    remoteNote: 'जैसा सेवा ने भेजा। एआई सिर्फ़ ऊपर वाला विवरण देखता है।',
    reviewNote: 'देखें कि टूल क्या करता है, नीचे उसकी नीति तय करें, फिर चालू करें।',
    review: 'समीक्षा करें और चालू करें',
    settings: 'सेटिंग',
    on: 'यह टूल एजेंटों को दें',
    onHint: 'बंद होने पर कोई एजेंट इसे नहीं बुला सकता।',
    risk: 'यह क्या कर सकता है',
    approver: 'कौन मंज़ूरी देता है',
    approverCustomer: 'ग्राहक',
    approverCustomerHint: 'सिर्फ़ उनके अपने कम जोखिम वाले अनुरोधों के लिए, चैट में।',
    approverStaff: 'टीम का सदस्य',
    approverStaffHint: 'जिनके पास “एआई कार्रवाइयाँ मंज़ूर करें” और नीचे वाली अनुमति है।',
    autopilot: 'बिना मंज़ूरी चल सकता है',
    autopilotHint:
      'सिर्फ़ पढ़ना और कम जोखिम वाले बदलाव, सीमाओं में। पैसा और न लौटने वाली कार्रवाइयों को हमेशा टीम की मंज़ूरी चाहिए।',
    stepUp: 'ग्राहक पहले अपनी पहचान पक्की करता है',
    stepUpHint: 'उन्होंने पिछले कुछ मिनटों में वेबसाइट पर साइन इन किया हो।',
    requiredPermission: 'मंज़ूरी देने वालों के लिए अतिरिक्त अनुमति',
    requiredPermissionHint: 'जैसे commerce.refunds.approve।',
    approvalMinutes: 'मंज़ूरी कितनी देर खुली रहे (मिनट)',
    approvalMinutesHint: 'ख़ाली: ग्राहकों के लिए 30 मिनट, टीम के लिए 24 घंटे।',
    timeout: 'समय सीमा (सेकंड)',
    maxOutput: 'सबसे बड़ा जवाब (KB)',
    save: 'बदलाव सहेजें',
    saved: 'सहेजा गया। नीति बदलने से इस टूल की खुली मंज़ूरियाँ रद्द हो गईं।',
    conflict: 'किसी और ने यह टूल पहले बदला। नया संस्करण दिख रहा है; अपना बदलाव दोबारा करें।',
    stop: 'यह टूल रोकें',
    stopTitle: '“{{tool}}” रोकें?',
    stopBody:
      'स्विच हटने तक हर कॉल मना होगी और मंज़ूर कार्रवाइयाँ नहीं भेजी जाएँगी। भेजी जा चुकी कार्रवाइयों की जाँच जारी रहती है।',
    stopReason: 'कारण',
    stoppedNow: '{{tool}} रोका गया।',
    stopped: 'रोका गया: एआई रोकें देखें',
    recent: 'हाल की कार्रवाइयाँ',
  },
  errors: {
    number: '{{min}} से {{max}} तक कोई पूर्ण संख्या लिखें।',
    hosts: 'ज़्यादा से ज़्यादा 20 होस्ट।',
    highRiskApprover: 'पैसा और न लौटने वाली कार्रवाइयाँ टीम ही मंज़ूर कर सकती है।',
    highRiskAutopilot: 'पैसा और न लौटने वाली कार्रवाइयाँ कभी बिना मंज़ूरी नहीं चलतीं।',
    label: 'नाम लिखें।',
    url: 'सर्वर का पूरा पता लिखें, https:// से शुरू करके।',
    token: 'टोकन कम से कम 8 अक्षरों का होता है।',
    reason: 'कुछ शब्दों में कारण बताएँ।',
  },
  servers: {
    title: 'जुड़ी सेवाएँ (MCP)',
    description:
      'HTTPS पर दूरस्थ MCP सर्वर। नए टूल समीक्षा का इंतज़ार करते हैं, और सेवा की कोई बात एआई तक निर्देश बनकर नहीं पहुँचती।',
    loading: 'सेवाएँ लोड हो रही हैं',
    empty: 'कोई सेवा नहीं जुड़ी।',
    connect: 'सेवा जोड़ें',
    columns: { name: 'सेवा' },
    status: { active: 'सक्रिय', disabled: 'बंद' },
    toolCount: 'टूल: {{total}}',
    pending: 'समीक्षा बाक़ी: {{total}}',
    checked: 'टूल {{time}} को जाँचे गए',
    never: 'टूल अभी जाँचे नहीं गए',
    lastError: 'पिछली जाँच विफल रही: {{error}}',
    sync: 'टूल जाँचें',
    syncLabel: '{{name}} के टूल जाँचें',
    synced: 'जाँचा गया: {{added}} नए, {{changed}} बदले, {{removed}} हटाए गए।',
    skipped: 'सुरक्षित रूप से नहीं जोड़े गए: {{total}}।',
    turnOff: 'बंद करें',
    turnOn: 'चालू करें',
    dialogTitle: 'सेवा जोड़ें',
    dialogDescription:
      'Hamnivo, MCP सर्वरों से Streamable HTTP पर बात करता है। किसी एजेंट के इस्तेमाल से पहले उनके टूल समीक्षा का इंतज़ार करते हैं।',
    label: 'नाम',
    url: 'सर्वर का पता',
    urlHint: 'जैसे https://orders.example.com/mcp',
    token: 'एक्सेस टोकन',
    tokenHint: 'एन्क्रिप्ट करके रखा जाता है। सिर्फ़ उसके आख़िरी अक्षर दिखते हैं।',
    submit: 'जोड़ें',
    connected: '{{name}} जुड़ गई। अब उसके टूल जाँचें।',
  },
  settings: {
    title: 'अपने-आप होने वाली कार्रवाइयाँ',
    description: 'उन कार्रवाइयों की सीमाएँ जो एआई बिना मंज़ूरी कर सकता है। किल स्विच इन्हें तुरंत रोकते हैं।',
    loading: 'सेटिंग लोड हो रही हैं',
    enabled: 'अपने-आप कार्रवाइयों की अनुमति दें',
    enabledHint: 'बंद होने पर हर बदलाव मंज़ूरी का इंतज़ार करता है।',
    limit: 'हर दिन ज़्यादा से ज़्यादा अपने-आप कार्रवाइयाँ',
    limitHint: 'पूरे वर्कस्पेस के लिए। इसके बाद बदलाव मंज़ूरी का इंतज़ार करते हैं और खोज रुक जाती है।',
    save: 'सहेजें',
    saved: 'सेटिंग सहेजी गईं।',
  },
  log: {
    title: 'हाल की एआई कार्रवाइयाँ',
    description: 'एआई ने क्या सुझाया, किसने फ़ैसला किया, और रसीद क्या कहती है।',
    loading: 'कार्रवाइयाँ लोड हो रही हैं',
    empty: 'अभी कोई कार्रवाई नहीं।',
    open: 'बातचीत खोलें',
    columns: {
      when: 'कब',
      action: 'कार्रवाई',
      state: 'स्थिति',
      decided: 'किसने तय किया',
    },
    by: {
      customer: 'ग्राहक',
      staff: 'टीम सदस्य',
      autopilot: 'अपने-आप',
    },
  },
  grants: {
    title: 'टूल',
    hint: 'यह एजेंट क्या इस्तेमाल कर सकता है। हर टूल की अपनी नीति तय करती है कि मंज़ूरी कौन देगा।',
    hidden: 'आप इस ब्रांड के टूल नहीं देख सकते।',
    none: 'इस ब्रांड के अभी कोई टूल नहीं हैं।',
    manage: 'टूल संभालें',
    autopilot: 'बिना मंज़ूरी चल सकता है',
    autopilotHint: 'बंद: यह एजेंट हमेशा पूछता है, तब भी जब टूल ख़ुद चल सकता हो।',
    inReview: 'यह टूल समीक्षा का इंतज़ार कर रहा है; तब तक एजेंट इसे इस्तेमाल नहीं कर सकता।',
    unknown: 'एक टूल जो अब यहाँ मौजूद नहीं:',
    remove: 'हटाएँ',
    tooMany: 'एक एजेंट ज़्यादा से ज़्यादा 30 टूल इस्तेमाल कर सकता है।',
    maxCalls: 'हर संदेश पर टूल कॉल',
    maxCallsHint: '1 से 10 तक। इससे ज़्यादा कॉल मना होती हैं।',
    maxAutopilot: 'हर बातचीत में अपने-आप कार्रवाइयाँ',
    maxAutopilotHint: '0 से 50 तक। इसके बाद बदलाव मंज़ूरी का इंतज़ार करते हैं।',
  },
  sims: {
    title: 'प्रकाशित करने से पहले जाँचें',
    description:
      'नमूना बातचीत को इस ड्राफ़्ट और प्रकाशित संस्करण पर, टेस्ट मॉडल और नकली टूल के साथ दोबारा चलाएँ। कुछ नहीं भेजा जाता और ग्राहक डेटा इस्तेमाल नहीं होता।',
    loading: 'टेस्ट लोड हो रहे हैं',
    empty: 'अभी कोई टेस्ट सूट नहीं।',
    add: 'टेस्ट सूट जोड़ें',
    addTitle: 'टेस्ट सूट जोड़ें',
    editTitle: 'टेस्ट सूट बदलें',
    dialogDescription: 'हर केस में ग्राहक के संदेश, हर टूल का जवाब और आपकी उम्मीद लिखी होती है।',
    name: 'नाम',
    requiredLabel: 'ड्राफ़्ट पास होने तक प्रकाशन रुका रहे',
    requiredHint: 'लाइव होने से पहले यही ड्राफ़्ट इस सूट में पास होना चाहिए।',
    cases: 'केस (JSON)',
    casesHint:
      'केस की सूची जिसमें id, name, messages, tools (नकली जवाब) और expect (नतीजा, कार्रवाइयाँ) हों।',
    save: 'सूट सहेजें',
    edit: 'बदलें',
    delete: 'हटाएँ',
    deleteTitle: 'टेस्ट सूट “{{name}}” हटाएँ?',
    deleteBody: 'इसके नतीजे भी हट जाएँगे। प्रकाशन अब इसका इंतज़ार नहीं करेगा।',
    run: 'इस ड्राफ़्ट पर चलाएँ',
    runLabel: '{{name}} को इस ड्राफ़्ट पर चलाएँ',
    required: 'प्रकाशन से पहले ज़रूरी',
    optional: 'वैकल्पिक',
    caseCount: 'केस: {{total}}',
    lastRun: 'आख़िरी बार {{time}} को चलाया: {{status}}',
    notRun: 'इस ड्राफ़्ट पर अभी नहीं चलाया गया',
    noDraft: 'पहले ड्राफ़्ट सहेजें; टेस्ट ड्राफ़्ट पर चलते हैं।',
    results: 'नतीजे',
    summary: 'केस: {{cases}} · बदले: {{changed}} · विफल: {{failed}}',
    testOnly: 'सिर्फ़ टेस्ट: कुछ नहीं भेजा गया',
    draft: 'यह ड्राफ़्ट',
    published: 'प्रकाशित संस्करण',
    noPublished: 'अभी कुछ प्रकाशित नहीं।',
    noActions: 'कोई कार्रवाई नहीं',
    changes: 'प्रकाशित संस्करण से बदलाव',
    noChanges: 'कोई बदलाव नहीं',
    failures: 'उम्मीदें पूरी नहीं हुईं',
    replies: 'ड्राफ़्ट के जवाब',
    status: { passed: 'पास', failed: 'विफल' },
    outcomes: {
      replied: 'जवाब दिया',
      handoff: 'व्यक्ति को सौंपा',
      blocked: 'गार्ड ने रोका',
      error: 'त्रुटि',
    },
    decisions: {
      executed: 'हो गया',
      approval: 'मंज़ूरी माँगता है',
      denied: 'मना किया',
      invalid: 'अधूरा अनुरोध',
      failed: 'विफल',
    },
    errors: {
      name: 'नाम लिखें।',
      cases: 'कम से कम एक केस वाली JSON सूची लिखें।',
    },
  },
  inbox: {
    title: 'एआई कार्रवाइयाँ',
    empty: 'एआई ने यहाँ कोई कार्रवाई नहीं सुझाई।',
    from: 'से',
    to: 'तक',
    amount: 'राशि',
    receipt: 'रसीद: {{outcome}}',
    receiptOutcomes: {
      pending: 'भेजा गया',
      succeeded: 'हो गया',
      failed: 'नहीं हुआ',
      unknown: 'पक्का नहीं',
    },
    decided: '{{who}} ने {{decision}} · {{time}}',
    decisions: { approved: 'मंज़ूर किया', declined: 'मना किया' },
    customerApproves: 'ग्राहक इसे चैट में मंज़ूर करता है।',
    staffApproves: 'टीम का सदस्य इसे मंज़ूर करता है।',
    stepUp: 'टीम का सदस्य नए साइन-इन के बाद इसे मंज़ूर करता है।',
    expires: '{{time}} तक खुला।',
    notConfirmed: 'भेजा गया, पर नतीजा पक्का नहीं। इसे हाथ से दोबारा न करें।',
    approve: 'मंज़ूर करें',
    decline: 'मना करें',
    approved: 'मंज़ूर। भेजा जा रहा है।',
    declined: 'मना किया। कुछ नहीं बदला।',
    check: 'स्थिति जाँचें',
    checking: 'सेवा से जाँच हो रही है।',
    resolve: 'नतीजा दर्ज करें',
    resolveTitle: 'जो हुआ वह दर्ज करें',
    resolveDescription: 'सिर्फ़ तब, जब Hamnivo ख़ुद सेवा से जाँच न सके। कुछ भी दोबारा नहीं भेजा जाता।',
    resolveOutcome: 'क्या हुआ',
    resolveDone: 'हो गया था',
    resolveNotDone: 'नहीं हुआ था',
    resolveNote: 'आपने क्या देखा (ऑडिट लॉग में रखा जाता है)',
    resolveNoteError: 'कुछ शब्दों में बताएँ कि आपने क्या देखा।',
    resolveSubmit: 'नतीजा दर्ज करें',
    resolved: 'नतीजा दर्ज हुआ।',
  },
  handoff: {
    title: 'हैंडऑफ़ सारांश',
    none: 'एआई ने यह बातचीत आगे नहीं सौंपी।',
    epoch: 'हैंडओवर {{epoch}}',
    request: 'ग्राहक का अनुरोध',
    attempts: 'एआई ने क्या किया',
    pending: 'अभी खुला',
    pendingStates: {
      awaiting_approval: 'मंज़ूरी का इंतज़ार',
      approved: 'मंज़ूर, अभी भेजा नहीं गया',
      executing: 'भेजा गया, नतीजे का इंतज़ार',
      unknown: 'नतीजा पक्का नहीं, जाँच जारी',
      superseded: 'हैंडओवर से रद्द',
    },
    facts: 'ग्राहक की जानकारी',
    sources: {
      tool: 'टूल का नतीजा',
      memory: 'ग्राहक की मेमोरी',
      identity: 'वेबसाइट से सत्यापित',
      language: 'भाषा सेटिंग',
      order: 'ऑर्डर रिकॉर्ड',
    },
    recorded: '{{time}} को Hamnivo के रिकॉर्ड से दर्ज।',
  },
  runs: {
    stop: 'यह रन रोकें',
    stopTitle: 'यह एआई रन रोकें?',
    stopBody:
      'चल रही टूल कॉल रद्द होंगी और कोई जवाब पोस्ट नहीं होगा। पहले भेजे गए बदलाव की जाँच होती है, उसे कभी दोबारा नहीं भेजा जाता।',
    stopped: 'रोकने का अनुरोध भेजा गया।',
  },
};

export const toolsUr: ToolsText = {
  nav: 'ٹولز اور اجازتیں',
  title: 'ٹولز اور اجازتیں',
  description:
    'طے کریں کہ آپ کے اے آئی ایجنٹ کیا کر سکتے ہیں، کون سا ڈیٹا استعمال کر سکتے ہیں، اور کب کسی شخص کی منظوری پہلے ضروری ہے۔',
  brand: 'برانڈ',
  noBrands: 'پہلے ایک برانڈ شامل کریں۔',
  loading: 'ٹولز لوڈ ہو رہے ہیں',
  optional: 'اختیاری',
  agent: 'ایجنٹ',
  agentNone: 'اس برانڈ کے لیے ابھی کوئی اے آئی ایجنٹ سیٹ نہیں۔',
  agentPublished: 'شائع شدہ v{{version}}',
  agentDraft: 'شائع نہیں',
  editGrants: 'اس ایجنٹ کے ٹولز چنیں',
  noManage: 'آپ دیکھ سکتے ہیں، مگر ٹولز بدلنا صرف ان کا کام ہے جو ٹولز سنبھالتے ہیں۔',
  killScope: 'ٹول: {{name}}',
  risk: {
    read: 'ڈیٹا پڑھتا ہے',
    write: 'ڈیٹا بدلتا ہے',
    money: 'رقم منتقل کرتا ہے',
    irreversible: 'واپس نہیں ہو سکتا',
  },
  permission: {
    automatic: 'خود بخود چلتا ہے',
    customer: 'گاہک منظوری دیتا ہے',
    staff: 'ٹیم منظوری دیتی ہے',
    review: 'جائزے کا انتظار',
    off: 'بند',
    stopped: 'روکا گیا',
  },
  permissionHint: {
    automatic: 'نیچے دی گئی حدود میں۔ ہر تبدیلی کی رسید بنتی ہے۔',
    customer: 'گاہک پہلے چیٹ میں بالکل اسی تبدیلی کی منظوری دیتا ہے۔',
    staff: 'اجازت والا ٹیم ممبر پہلے منظوری دیتا ہے۔',
    review: 'کسی شخص کے جائزے تک اسے کوئی نہیں بلا سکتا۔',
    off: 'کسی ایجنٹ کو نہیں دیا جاتا۔',
    stopped: 'کِل سوئچ ہٹنے تک ہر کال رد ہوتی ہے۔',
  },
  states: {
    awaiting_approval: 'منظوری کا انتظار',
    approved: 'منظور',
    executing: 'کیا جا رہا ہے',
    completed: 'ہو گیا',
    failed: 'نہیں ہوا',
    unknown: 'نتیجہ تصدیق شدہ نہیں',
    declined: 'انکار',
    expired: 'مدت ختم',
    superseded: 'بدل دیا گیا',
    cancelled: 'روکا گیا',
    denied: 'رد',
  },
  list: {
    title: 'ٹولز',
    description:
      'ہر کال ہوتے وقت دوبارہ جانچی جاتی ہے: ٹول کی حالت اور پالیسی، کِل سوئچ، حدود اور گفتگو کون سنبھال رہا ہے۔',
    builtin: 'Hamnivo میں شامل',
    from: '{{server}} سے',
    emptyTitle: 'ابھی کوئی ٹول نہیں',
    emptyDescription:
      'کوئی شامل ٹول جوڑیں یا کوئی سروس جوڑیں، تاکہ اے آئی معلومات دیکھ سکے یا تبدیلیاں مانگ سکے۔',
    open: '{{tool}} کھولیں',
    columns: {
      tool: 'ٹول',
      scope: 'یہ کیا کر سکتا ہے',
      permission: 'اجازت',
      changed: 'آخری تبدیلی',
      details: 'تفصیل',
    },
  },
  add: {
    button: 'شامل ٹول جوڑیں',
    title: 'شامل ٹول جوڑیں',
    description:
      'شامل ٹولز Hamnivo کے اپنے ریکارڈ پر، یا ڈیمو سیٹ اپ میں ڈیمو آرڈر سروس پر کام کرتے ہیں۔',
    label: 'ٹول',
    choose: 'ٹول چنیں',
    demo: 'ڈیمو',
    added: 'پہلے سے جوڑا گیا',
    unavailable: 'یہاں دستیاب نہیں',
    submit: 'ٹول جوڑیں',
    done: '{{tool}} جوڑ دیا گیا۔',
  },
  drawer: {
    permission: 'اجازت',
    revision: 'نظرثانی {{revision}}',
    reviewed: '{{time}} کو جائزہ ہوا',
    service: 'جڑی ہوئی سروس',
    credential: 'کریڈینشل',
    noCredential: 'کوئی نہیں',
    hosts: 'اجازت یافتہ ہوسٹ',
    hostsHint: 'ہر لائن میں ایک ہوسٹ، جیسے orders.example.com۔ کالیں صرف انہی تک پہنچ سکتی ہیں۔',
    remote: 'سروس کیا کہتی ہے (اے آئی کو کبھی نہیں دکھایا جاتا)',
    remoteNote: 'جیسا سروس نے بھیجا۔ اے آئی صرف اوپر والی تفصیل دیکھتا ہے۔',
    reviewNote: 'دیکھیں کہ ٹول کیا کرتا ہے، نیچے اس کی پالیسی طے کریں، پھر چالو کریں۔',
    review: 'جائزہ لیں اور چالو کریں',
    settings: 'سیٹنگز',
    on: 'یہ ٹول ایجنٹوں کو دیں',
    onHint: 'بند ہونے پر کوئی ایجنٹ اسے نہیں بلا سکتا۔',
    risk: 'یہ کیا کر سکتا ہے',
    approver: 'کون منظوری دیتا ہے',
    approverCustomer: 'گاہک',
    approverCustomerHint: 'صرف ان کی اپنی کم خطرے والی درخواستوں کے لیے، چیٹ میں۔',
    approverStaff: 'ٹیم کا رکن',
    approverStaffHint: 'جن کے پاس “اے آئی کارروائیاں منظور کریں” اور نیچے والی اجازت ہے۔',
    autopilot: 'بغیر منظوری چل سکتا ہے',
    autopilotHint:
      'صرف پڑھنا اور کم خطرے والی تبدیلیاں، حدود میں۔ رقم اور ناقابلِ واپسی کارروائیوں کو ہمیشہ ٹیم کی منظوری چاہیے۔',
    stepUp: 'گاہک پہلے اپنی شناخت کی تصدیق کرتا ہے',
    stepUpHint: 'انہوں نے پچھلے چند منٹوں میں ویب سائٹ پر سائن اِن کیا ہو۔',
    requiredPermission: 'منظوری دینے والوں کے لیے اضافی اجازت',
    requiredPermissionHint: 'جیسے commerce.refunds.approve۔',
    approvalMinutes: 'منظوری کتنی دیر کھلی رہے (منٹ)',
    approvalMinutesHint: 'خالی: گاہکوں کے لیے 30 منٹ، ٹیم کے لیے 24 گھنٹے۔',
    timeout: 'وقت کی حد (سیکنڈ)',
    maxOutput: 'سب سے بڑا جواب (KB)',
    save: 'تبدیلیاں محفوظ کریں',
    saved: 'محفوظ ہو گیا۔ پالیسی بدلنے سے اس ٹول کی کھلی منظوریاں منسوخ ہو گئیں۔',
    conflict: 'کسی اور نے یہ ٹول پہلے بدلا۔ تازہ ترین ورژن دکھایا گیا ہے؛ اپنی تبدیلی دوبارہ کریں۔',
    stop: 'یہ ٹول روکیں',
    stopTitle: '“{{tool}}” روکیں؟',
    stopBody:
      'سوئچ ہٹنے تک ہر کال رد ہوگی اور منظور شدہ کارروائیاں نہیں بھیجی جائیں گی۔ بھیجی جا چکی کارروائیوں کی جانچ جاری رہتی ہے۔',
    stopReason: 'وجہ',
    stoppedNow: '{{tool}} روک دیا گیا۔',
    stopped: 'روکا گیا: اے آئی روکیں دیکھیں',
    recent: 'حالیہ کارروائیاں',
  },
  errors: {
    number: '{{min}} سے {{max}} تک کوئی مکمل عدد لکھیں۔',
    hosts: 'زیادہ سے زیادہ 20 ہوسٹ۔',
    highRiskApprover: 'رقم اور ناقابلِ واپسی کارروائیاں صرف ٹیم منظور کر سکتی ہے۔',
    highRiskAutopilot: 'رقم اور ناقابلِ واپسی کارروائیاں کبھی بغیر منظوری نہیں چلتیں۔',
    label: 'نام لکھیں۔',
    url: 'سرور کا پورا پتہ لکھیں، https:// سے شروع کر کے۔',
    token: 'ٹوکن کم از کم 8 حروف کا ہوتا ہے۔',
    reason: 'چند الفاظ میں وجہ بتائیں۔',
  },
  servers: {
    title: 'جڑی ہوئی سروسز (MCP)',
    description:
      'HTTPS پر دور کے MCP سرور۔ نئے ٹولز جائزے کا انتظار کرتے ہیں، اور سروس کی کوئی بات اے آئی تک ہدایت بن کر نہیں پہنچتی۔',
    loading: 'سروسز لوڈ ہو رہی ہیں',
    empty: 'کوئی سروس نہیں جڑی۔',
    connect: 'سروس جوڑیں',
    columns: { name: 'سروس' },
    status: { active: 'فعال', disabled: 'بند' },
    toolCount: 'ٹولز: {{total}}',
    pending: 'جائزے کا انتظار: {{total}}',
    checked: 'ٹولز {{time}} کو جانچے گئے',
    never: 'ٹولز ابھی نہیں جانچے گئے',
    lastError: 'پچھلی جانچ ناکام رہی: {{error}}',
    sync: 'ٹولز جانچیں',
    syncLabel: '{{name}} کے ٹولز جانچیں',
    synced: 'جانچ ہو گئی: {{added}} نئے، {{changed}} بدلے، {{removed}} ہٹائے گئے۔',
    skipped: 'محفوظ طریقے سے نہیں جوڑے گئے: {{total}}۔',
    turnOff: 'بند کریں',
    turnOn: 'چالو کریں',
    dialogTitle: 'سروس جوڑیں',
    dialogDescription:
      'Hamnivo، MCP سرورز سے Streamable HTTP پر بات کرتا ہے۔ کسی ایجنٹ کے استعمال سے پہلے ان کے ٹولز جائزے کا انتظار کرتے ہیں۔',
    label: 'نام',
    url: 'سرور کا پتہ',
    urlHint: 'جیسے https://orders.example.com/mcp',
    token: 'رسائی ٹوکن',
    tokenHint: 'خفیہ کر کے رکھا جاتا ہے۔ صرف اس کے آخری حروف دکھتے ہیں۔',
    submit: 'جوڑیں',
    connected: '{{name}} جڑ گئی۔ اب اس کے ٹولز جانچیں۔',
  },
  settings: {
    title: 'خودکار کارروائیاں',
    description:
      'ان کارروائیوں کی حدود جو اے آئی بغیر منظوری کر سکتا ہے۔ کِل سوئچ انہیں فوراً روکتے ہیں۔',
    loading: 'سیٹنگز لوڈ ہو رہی ہیں',
    enabled: 'خودکار کارروائیوں کی اجازت دیں',
    enabledHint: 'بند ہونے پر ہر تبدیلی منظوری کا انتظار کرتی ہے۔',
    limit: 'روزانہ زیادہ سے زیادہ خودکار کارروائیاں',
    limitHint:
      'پورے ورک اسپیس کے لیے۔ اس کے بعد تبدیلیاں منظوری کا انتظار کرتی ہیں اور تلاش رک جاتی ہے۔',
    save: 'محفوظ کریں',
    saved: 'سیٹنگز محفوظ ہو گئیں۔',
  },
  log: {
    title: 'حالیہ اے آئی کارروائیاں',
    description: 'اے آئی نے کیا تجویز کیا، کس نے فیصلہ کیا، اور رسید کیا کہتی ہے۔',
    loading: 'کارروائیاں لوڈ ہو رہی ہیں',
    empty: 'ابھی کوئی کارروائی نہیں۔',
    open: 'گفتگو کھولیں',
    columns: {
      when: 'کب',
      action: 'کارروائی',
      state: 'حالت',
      decided: 'کس نے طے کیا',
    },
    by: {
      customer: 'گاہک',
      staff: 'ٹیم کا رکن',
      autopilot: 'خودکار',
    },
  },
  grants: {
    title: 'ٹولز',
    hint: 'یہ ایجنٹ کیا استعمال کر سکتا ہے۔ ہر ٹول کی اپنی پالیسی طے کرتی ہے کہ منظوری کون دے گا۔',
    hidden: 'آپ اس برانڈ کے ٹولز نہیں دیکھ سکتے۔',
    none: 'اس برانڈ کے ابھی کوئی ٹولز نہیں۔',
    manage: 'ٹولز سنبھالیں',
    autopilot: 'بغیر منظوری چل سکتا ہے',
    autopilotHint: 'بند: یہ ایجنٹ ہمیشہ پوچھتا ہے، تب بھی جب ٹول خود چل سکتا ہو۔',
    inReview: 'یہ ٹول جائزے کا انتظار کر رہا ہے؛ تب تک ایجنٹ اسے استعمال نہیں کر سکتا۔',
    unknown: 'ایک ٹول جو اب یہاں موجود نہیں:',
    remove: 'ہٹائیں',
    tooMany: 'ایک ایجنٹ زیادہ سے زیادہ 30 ٹولز استعمال کر سکتا ہے۔',
    maxCalls: 'ہر پیغام پر ٹول کالز',
    maxCallsHint: '1 سے 10 تک۔ اس سے زیادہ کالز رد ہوتی ہیں۔',
    maxAutopilot: 'ہر گفتگو میں خودکار کارروائیاں',
    maxAutopilotHint: '0 سے 50 تک۔ اس کے بعد تبدیلیاں منظوری کا انتظار کرتی ہیں۔',
  },
  sims: {
    title: 'شائع کرنے سے پہلے جانچیں',
    description:
      'نمونہ گفتگو کو اس ڈرافٹ اور شائع شدہ ورژن پر، ٹیسٹ ماڈل اور فرضی ٹولز کے ساتھ دوبارہ چلائیں۔ کچھ نہیں بھیجا جاتا اور گاہک کا ڈیٹا استعمال نہیں ہوتا۔',
    loading: 'ٹیسٹ لوڈ ہو رہے ہیں',
    empty: 'ابھی کوئی ٹیسٹ سوٹ نہیں۔',
    add: 'ٹیسٹ سوٹ جوڑیں',
    addTitle: 'ٹیسٹ سوٹ جوڑیں',
    editTitle: 'ٹیسٹ سوٹ بدلیں',
    dialogDescription: 'ہر کیس میں گاہک کے پیغام، ہر ٹول کا جواب اور آپ کی توقع لکھی ہوتی ہے۔',
    name: 'نام',
    requiredLabel: 'ڈرافٹ پاس ہونے تک اشاعت رکی رہے',
    requiredHint: 'لائیو ہونے سے پہلے یہی ڈرافٹ اس سوٹ میں پاس ہونا چاہیے۔',
    cases: 'کیس (JSON)',
    casesHint:
      'کیسوں کی فہرست جس میں id، name، messages، tools (فرضی جواب) اور expect (نتیجہ، کارروائیاں) ہوں۔',
    save: 'سوٹ محفوظ کریں',
    edit: 'بدلیں',
    delete: 'حذف کریں',
    deleteTitle: 'ٹیسٹ سوٹ “{{name}}” حذف کریں؟',
    deleteBody: 'اس کے نتائج بھی حذف ہو جائیں گے۔ اشاعت اب اس کا انتظار نہیں کرے گی۔',
    run: 'اس ڈرافٹ پر چلائیں',
    runLabel: '{{name}} کو اس ڈرافٹ پر چلائیں',
    required: 'اشاعت سے پہلے ضروری',
    optional: 'اختیاری',
    caseCount: 'کیس: {{total}}',
    lastRun: 'آخری بار {{time}} کو چلایا: {{status}}',
    notRun: 'اس ڈرافٹ پر ابھی نہیں چلایا گیا',
    noDraft: 'پہلے ڈرافٹ محفوظ کریں؛ ٹیسٹ ڈرافٹ پر چلتے ہیں۔',
    results: 'نتائج',
    summary: 'کیس: {{cases}} · بدلے: {{changed}} · ناکام: {{failed}}',
    testOnly: 'صرف ٹیسٹ: کچھ نہیں بھیجا گیا',
    draft: 'یہ ڈرافٹ',
    published: 'شائع شدہ ورژن',
    noPublished: 'ابھی کچھ شائع نہیں۔',
    noActions: 'کوئی کارروائی نہیں',
    changes: 'شائع شدہ ورژن سے تبدیلیاں',
    noChanges: 'کوئی تبدیلی نہیں',
    failures: 'توقعات پوری نہیں ہوئیں',
    replies: 'ڈرافٹ کے جواب',
    status: { passed: 'پاس', failed: 'ناکام' },
    outcomes: {
      replied: 'جواب دیا',
      handoff: 'شخص کے حوالے',
      blocked: 'گارڈ نے روکا',
      error: 'خرابی',
    },
    decisions: {
      executed: 'ہو گیا',
      approval: 'منظوری مانگتا ہے',
      denied: 'رد',
      invalid: 'نامکمل درخواست',
      failed: 'ناکام',
    },
    errors: {
      name: 'نام لکھیں۔',
      cases: 'کم از کم ایک کیس والی JSON فہرست لکھیں۔',
    },
  },
  inbox: {
    title: 'اے آئی کارروائیاں',
    empty: 'اے آئی نے یہاں کوئی کارروائی تجویز نہیں کی۔',
    from: 'سے',
    to: 'تک',
    amount: 'رقم',
    receipt: 'رسید: {{outcome}}',
    receiptOutcomes: {
      pending: 'بھیجا گیا',
      succeeded: 'ہو گیا',
      failed: 'نہیں ہوا',
      unknown: 'تصدیق نہیں',
    },
    decided: '{{who}} نے {{decision}} · {{time}}',
    decisions: { approved: 'منظور کیا', declined: 'انکار کیا' },
    customerApproves: 'گاہک اسے چیٹ میں منظور کرتا ہے۔',
    staffApproves: 'ٹیم کا رکن اسے منظور کرتا ہے۔',
    stepUp: 'ٹیم کا رکن نئے سائن اِن کے بعد اسے منظور کرتا ہے۔',
    expires: '{{time}} تک کھلا۔',
    notConfirmed: 'بھیج دیا گیا، مگر نتیجہ تصدیق شدہ نہیں۔ اسے ہاتھ سے دوبارہ نہ کریں۔',
    approve: 'منظور کریں',
    decline: 'انکار کریں',
    approved: 'منظور۔ بھیجا جا رہا ہے۔',
    declined: 'انکار کر دیا۔ کچھ نہیں بدلا۔',
    check: 'حالت جانچیں',
    checking: 'سروس سے جانچ ہو رہی ہے۔',
    resolve: 'نتیجہ درج کریں',
    resolveTitle: 'جو ہوا وہ درج کریں',
    resolveDescription:
      'صرف تب، جب Hamnivo خود سروس سے جانچ نہ سکے۔ کچھ بھی دوبارہ نہیں بھیجا جاتا۔',
    resolveOutcome: 'کیا ہوا',
    resolveDone: 'ہو گیا تھا',
    resolveNotDone: 'نہیں ہوا تھا',
    resolveNote: 'آپ نے کیا دیکھا (آڈٹ لاگ میں رکھا جاتا ہے)',
    resolveNoteError: 'چند الفاظ میں بتائیں کہ آپ نے کیا دیکھا۔',
    resolveSubmit: 'نتیجہ درج کریں',
    resolved: 'نتیجہ درج ہو گیا۔',
  },
  handoff: {
    title: 'حوالگی کا خلاصہ',
    none: 'اے آئی نے یہ گفتگو آگے نہیں سونپی۔',
    epoch: 'حوالگی {{epoch}}',
    request: 'گاہک کی درخواست',
    attempts: 'اے آئی نے کیا کیا',
    pending: 'ابھی کھلا',
    pendingStates: {
      awaiting_approval: 'منظوری کا انتظار',
      approved: 'منظور، ابھی بھیجا نہیں گیا',
      executing: 'بھیج دیا گیا، نتیجے کا انتظار',
      unknown: 'نتیجہ تصدیق شدہ نہیں، جانچ جاری',
      superseded: 'حوالگی سے منسوخ',
    },
    facts: 'گاہک کی معلومات',
    sources: {
      tool: 'ٹول کا نتیجہ',
      memory: 'گاہک کی یادداشت',
      identity: 'ویب سائٹ سے تصدیق شدہ',
      language: 'زبان کی سیٹنگ',
      order: 'آرڈر ریکارڈ',
    },
    recorded: '{{time}} کو Hamnivo کے ریکارڈ سے درج۔',
  },
  runs: {
    stop: 'یہ رن روکیں',
    stopTitle: 'یہ اے آئی رن روکیں؟',
    stopBody:
      'جاری ٹول کالز منسوخ ہوں گی اور کوئی جواب پوسٹ نہیں ہوگا۔ پہلے بھیجی گئی تبدیلی کی جانچ ہوتی ہے، اسے کبھی دوبارہ نہیں بھیجا جاتا۔',
    stopped: 'روکنے کی درخواست بھیج دی گئی۔',
  },
};
